import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router';
import earthIcon from '@/assets/images/earth.svg';
import { useBrowserLanguage } from '@/hooks/useBrowserLanguage';
import { Language, useLanguage } from '@/hooks/useLanguage';
import styles from './LanguageSwitcher.module.scss';

export const LanguageSwitcher = () => {
  const { lang } = useParams<{ lang: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const { setLanguage: setStoredLanguage } = useBrowserLanguage();
  const { language: currentLanguageFromHook } = useLanguage();
  // Use language from URL if available, otherwise use language from hook (which uses localStorage)
  const currentLanguage: Language = lang === 'ar' || lang === 'en' ? lang : currentLanguageFromHook;

  const handleLanguageSwitch = useCallback(() => {
    const newLanguage: Language = currentLanguage === 'ar' ? 'en' : 'ar';

    // Always save language to localStorage
    setStoredLanguage(newLanguage);

    // Always update i18n language
    i18n.changeLanguage(newLanguage);

    // Update URL to reflect language change (if language is in URL)
    const currentPath = location.pathname;
    const searchParams = new URLSearchParams(location.search);

    // Remove itemId from search params when changing language to avoid modal issues
    searchParams.delete('itemId');

    // Replace language prefix in the URL if it exists
    const newPath = currentPath.replace(/^\/(ar|en)\//, `/${newLanguage}/`);

    // Only navigate if the path actually changed (i.e., language was in URL)
    if (newPath !== currentPath) {
      navigate(`${newPath}?${searchParams.toString()}`);
    }
  }, [currentLanguage, navigate, location, setStoredLanguage, i18n]);

  const otherLanguage = currentLanguage === 'ar' ? 'EN' : 'AR';

  return (
    <button
      className={styles.languageSwitcher}
      onClick={handleLanguageSwitch}
      aria-label={t('languageSwitcher.switchTo', { language: otherLanguage })}
      title={t('languageSwitcher.switchTo', { language: otherLanguage })}
      data-testid="language-switcher"
    >
      <span className={styles.text}>{otherLanguage}</span>
      <img src={earthIcon} alt={t('languageSwitcher.language')} className={styles.icon} />
    </button>
  );
};
